import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ResolutionsPanel from './ResolutionsPanel';
import './CorporateRecordsPanel.css';

/**
 * Corporate Records workspace — minute-book style shelves with top tabs.
 * Resolutions tab hosts the full ResolutionsPanel workflow; other tabs list
 * linked documents categorized from /api/opo/auditor/corporate-records/documents.
 */

const FALLBACK_TABS = [
  { id: 'resolutions', label: 'Resolutions', description: 'Board and shareholder resolutions', count: 0 },
  { id: 'board-minutes', label: 'Board minutes', description: 'Board meeting minutes', count: 0 },
  { id: 'schedules', label: 'Schedules', description: 'Shareholder agreement & schedules', count: 0 },
  { id: 'constitutional', label: 'Constitutional', description: 'Articles, bylaws, registrations', count: 0 },
  { id: 'directors', label: 'Directors & officers', description: 'Consents, registers, ISC', count: 0 },
  { id: 'share-capital', label: 'Share capital', description: 'Share register & certificates', count: 0 },
  { id: 'agm', label: 'AGM', description: 'Annual general meeting', count: 0 },
  { id: 'other', label: 'Other records', description: 'Other linked records', count: 0 },
];

function viewUrlFor(doc) {
  if (!doc?.file_path) return null;
  return `/api/opo/auditor/compliance-document/view?file=${encodeURIComponent(doc.file_path)}`;
}

function CorporateRecordsShelf({ category, documents, loading }) {
  const [selectedId, setSelectedId] = useState(null);
  const selected = documents.find((d) => d.id === selectedId) || null;

  useEffect(() => {
    setSelectedId(null);
  }, [category]);

  if (loading) {
    return <p className="cr-empty">Loading records…</p>;
  }

  if (!documents.length) {
    return (
      <div className="cr-empty-panel">
        <p className="cr-empty">No documents in this shelf yet.</p>
        <p className="cr-empty-hint">
          Files tagged to Corporate Records that match this category will appear here.
          Upload via the generic agent tools or link from the compliance checklist.
        </p>
      </div>
    );
  }

  return (
    <div className="cr-shelf">
      <div className="cr-shelf-list">
        <div className="cr-shelf-list-header">
          <span>{documents.length} document{documents.length === 1 ? '' : 's'}</span>
        </div>
        <div className="cr-shelf-items">
          {documents.map((doc) => (
            <button
              key={doc.id}
              type="button"
              className={`cr-shelf-item${selectedId === doc.id ? ' is-active' : ''}`}
              onClick={() => setSelectedId(doc.id)}
            >
              <span className="cr-shelf-item-label">{doc.label || doc.file_path}</span>
              <span className="cr-shelf-item-path">{doc.file_path}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="cr-shelf-preview">
        {selected ? (
          <>
            <div className="cr-shelf-preview-header">
              <div>
                <div className="cr-shelf-preview-title">{selected.label}</div>
                <div className="cr-shelf-preview-path">{selected.file_path}</div>
              </div>
              <div className="cr-shelf-preview-actions">
                {viewUrlFor(selected) && (
                  <a
                    className="cr-btn-primary"
                    href={viewUrlFor(selected)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open document →
                  </a>
                )}
                <a
                  className="cr-btn-secondary"
                  href={`/opo/document/${selected.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Document page
                </a>
              </div>
            </div>
            <p className="cr-shelf-preview-hint">
              Opens the on-disk file from the Auditor compliance library. Signed originals live under
              <code> OptiPrime AI Inc/01_auditor/</code>.
            </p>
          </>
        ) : (
          <p className="cr-empty">Select a document to preview.</p>
        )}
      </div>
    </div>
  );
}

export default function CorporateRecordsPanel() {
  const [tab, setTab] = useState('resolutions');
  const [categories, setCategories] = useState(FALLBACK_TABS);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    fetch('/api/opo/auditor/corporate-records/documents')
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d.categories) && d.categories.length) {
          setCategories(d.categories);
        }
        setDocuments(d.documents || []);
      })
      .catch(() => {
        setCategories(FALLBACK_TABS);
        setDocuments([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const activeMeta = categories.find((c) => c.id === tab) || FALLBACK_TABS[0];
  const shelfDocs = useMemo(
    () => documents.filter((d) => d.category === tab),
    [documents, tab]
  );

  return (
    <div className="cr-panel">
      <div className="cr-panel-header">
        <div>
          <h2 className="cr-panel-title">Corporate Records</h2>
          <p className="cr-panel-subtitle">
            Minute book for OptiPrime AI Inc. — constitution, resolutions, board minutes, schedules,
            share capital, and AGM package.
          </p>
        </div>
        <button type="button" className="cr-btn-ghost" onClick={reload} title="Reload">
          ↻ Refresh
        </button>
      </div>

      <div className="cr-tabs" role="tablist" aria-label="Corporate Records shelves">
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={tab === c.id}
            className={`cr-tab${tab === c.id ? ' is-active' : ''}`}
            onClick={() => setTab(c.id)}
          >
            <span className="cr-tab-label">{c.label}</span>
            {c.id !== 'resolutions' && (
              <span className="cr-tab-count">{c.count ?? 0}</span>
            )}
          </button>
        ))}
      </div>

      <div className="cr-tab-body">
        {tab === 'resolutions' ? (
          <ResolutionsPanel embedded />
        ) : (
          <>
            <div className="cr-shelf-intro">
              <h3 className="cr-shelf-intro-title">{activeMeta.label}</h3>
              <p className="cr-shelf-intro-desc">{activeMeta.description}</p>
            </div>
            <CorporateRecordsShelf
              category={tab}
              documents={shelfDocs}
              loading={loading}
            />
          </>
        )}
      </div>
    </div>
  );
}
